import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { connect } from 'react-redux'
import { map, unset, hasIn } from 'lodash'
import { Modal, ModalBody, ModalFooter } from 'components/modal'
import { TwoColumnResponsiveLayout } from 'components/presentation'
import { Form, Input, Checkbox } from 'components/forms'
import { SaveButton, InfoButton } from 'components/buttons'
import { markAsPurchased } from 'cart/actions'
import withBlinkyTitleBar from 'components/with-blinky-title-bar'

import RegItemNote from 'shared/reg-item-note'
import RegItemImage from 'registry/components/reg-item-image'
import * as css from './mark-purchased-modal.scss'
import { tracking, track } from 'lib/analytics'

@tracking()
class MarkPurchasedModal extends PureComponent {
  constructor(props) {
    super(props)
    this.handleChange = this.handleChange.bind(this)
    this.handleReservationToExclude = this.handleReservationToExclude.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  state = {
    error: false,
    isPurchased: true,
    orderNumber: null,
    storeName: this.props.storeName,
    orderNumberNotAvailable: false,
    reservationsToExclude: {},
  }

  handleChange(e) {
    this.setState({
      [e.target.name]: e.target.value,
    })
  }

  handleReservationToExclude(reservation, checked) {
    let { reservationsToExclude } = this.state

    if (checked) {
      unset(reservationsToExclude, reservation.id)
    } else {
      reservationsToExclude[reservation.id] = true
    }

    this.setState({ reservationsToExclude })
  }

  handleSubmit(e) {
    e.preventDefault()
    let purchaseInfo = this.state
    const { reservations, tracking: tracker } = this.props

    map(reservations, (res, idx) => {
      if (!hasIn(this.state.reservationsToExclude, res.id)) {
        this.props.markAsPurchased(res, purchaseInfo, {
          showSuccessMessage: false,
        })
        // event can NOT be handle using @tracking because this action cause multiple event
        tracker.trackEvent({
          event: track.reservationUpdated,
          reservation: res,
          eventType: res.isPurchased
            ? track.EventType.MARK_AS_PURCHASED
            : track.EventType.MARK_AS_UNPURCHASED,
        })
      }
    })
    this.props.onHide()
  }

  render() {
    const { onHide, registry, reservations, submitting, show, storeName } =
      this.props

    return (
      <Modal
        onHide={onHide}
        size="large"
        show={show}
        title={`Finished your ${storeName} purchase?`}
        subtitle={`Add your ${storeName} Order Number. This will help ${
          registry && registry.firstNames ? registry.firstNames : 'the family'
        } if they need to return a gift.`}
      >
        <ModalBody>
          <TwoColumnResponsiveLayout>
            <div className="mtm-xs pvm-md">
              <PurchasedRegItemsChecklist
                reservations={reservations}
                registry={registry}
                onCheck={this.handleReservationToExclude}
              />
            </div>
            <div className="mtm-xs pam-md">
              <Form
                onSubmit={this.handleSubmit}
                className="animated fadeIn animate-250ms"
              >
                <Input
                  groupClassName={'mbs'}
                  name="orderNumber"
                  label={`${storeName} Order Number`}
                  type="text"
                  help={`Not sure where to find it? Check your email from ${storeName}.`}
                  onChange={this.handleChange}
                  error={this.state.error}
                  required={!this.state.orderNumberNotAvailable}
                  placeholder="#000-0000000-0000000"
                />
                <Checkbox
                  label="I don't have an order number"
                  name="orderNumberNotAvailable"
                  onChange={() =>
                    this.setState({
                      orderNumberNotAvailable:
                        !this.state.orderNumberNotAvailable,
                    })
                  }
                />
                <ModalFooter>
                  <SaveButton
                    className="mbm"
                    block
                    submitting={submitting}
                    type="submit"
                  >
                    Save
                  </SaveButton>
                  <InfoButton block empty onClick={onHide}>
                    Not Now
                  </InfoButton>
                </ModalFooter>
              </Form>
            </div>
          </TwoColumnResponsiveLayout>
        </ModalBody>
      </Modal>
    )
  }
}

export default connect(null, { markAsPurchased })(
  withBlinkyTitleBar(MarkPurchasedModal)
)

class PurchasedRegItemsChecklist extends PureComponent {
  static contextTypes = {
    isMobile: PropTypes.bool,
  }

  state = {
    showAllRegItems: false,
  }

  handleShowAll() {
    this.setState({ showAllRegItems: !this.state.showAllRegItems })
  }

  render() {
    const { reservations, registry, onCheck } = this.props

    const maxVisibleRegItems = this.context.isMobile ? 2 : 3
    const regItemsToDisplay = this.state.showAllRegItems
      ? reservations
      : reservations.slice(0, maxVisibleRegItems)

    return (
      <React.Fragment>
        <div className={css.regItemList}>
          {map(regItemsToDisplay, (res) => {
            return (
              <div className={classNames(css.regItem, 'pvm')}>
                <RegItemImage
                  className="center-block img-responsive media-left"
                  src={res.regItem.imgUrl}
                />
                <div className="flex col">
                  <div
                    className={classNames('h6 mtn', {
                      mbn: !res.regItem.description,
                    })}
                  >
                    {res.regItem.title}
                  </div>
                  {res.regItem.description && (
                    <RegItemNote
                      className="h6 mvn"
                      description={res.regItem.description}
                      firstNames={registry && registry.firstNames}
                    />
                  )}
                  <Checkbox
                    label="I've Purchased This"
                    groupClassName="mts mbn"
                    onChange={(e) => onCheck(res, e.target.checked)}
                    defaultChecked
                  />
                </div>
              </div>
            )
          })}
        </div>
        {reservations.length > maxVisibleRegItems && (
          <div className="text-center mbm">
            <InfoButton
              empty
              pill
              size="small"
              onClick={this.handleShowAll.bind(this)}
            >
              {!this.state.showAllRegItems
                ? `+ ${reservations.length - maxVisibleRegItems} More`
                : `View Less`}
            </InfoButton>
          </div>
        )}
      </React.Fragment>
    )
  }
}
